import { fromNullable, Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ActiveProgramModule, ProgramSegment } from '../../../ProgramStore/Types';
import OutlineModule from '../OutlineModule';
import OutlineSegments from '../OutlineSegments';

interface Props {
  modules: ActiveProgramModule[];
  currentSegmentId: Maybe<number>;
  viewedSegmentReady: boolean;
  onNavigate: (segment: ProgramSegment) => void;
  latestAvailableSegmentId: Maybe<number>;
}

// Renders directly from the (live) modules prop so ring/percentComplete updates from a
// course-map reload are always reflected. `openModuleIds` holds only the learner's manual
// expansions; the module that contains the segment you're viewing is always open (so it
// follows you across module transitions), falling back to isCurrentModule before the
// viewed segment resolves.
const OutlineModules: React.FC<Props> = ({
  modules,
  currentSegmentId,
  viewedSegmentReady,
  onNavigate,
  latestAvailableSegmentId,
}) => {
  const [openModuleIds, setOpenModuleIds] = React.useState<Set<number>>(() => new Set());

  const toggle = (id: number) =>
    setOpenModuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const viewedModuleId = currentSegmentId
    .andThen((segmentId) =>
      fromNullable(modules.find((m) => m.segments.some((s) => s.id === segmentId))),
    )
    .map((m) => m.id);

  return (
    <ol>
      {modules.map((m) => {
        // Open the module you're viewing AND the backend's current module, so finishing
        // one module auto-expands the next even before you navigate into it.
        const isViewed = viewedModuleId.map((id) => id === m.id).getOrElseValue(false);
        const open = openModuleIds.has(m.id) || isViewed || m.isCurrentModule;
        return (
          <li key={`module-${m.id}`}>
            <ol>
              <OutlineModule
                programModule={m}
                open={open}
                isViewedModule={isViewed}
                toggle={toggle}
              />
              {open && !m.isGated && (
                <OutlineSegments
                  programModule={m}
                  currentSegmentId={currentSegmentId}
                  viewedSegmentReady={viewedSegmentReady}
                  onNavigate={onNavigate}
                  latestAvailableSegmentId={latestAvailableSegmentId}
                />
              )}
            </ol>
          </li>
        );
      })}
    </ol>
  );
};

export default observer(OutlineModules);
